import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ArrowDown, MapPin, Plane, Clock, Heart, Briefcase, Sparkles, Car } from "lucide-react";
import { IMAGES, CONTACT, SERVICES } from "@/data/site";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.25 + i * 0.12 },
  }),
};

function Reveal({ children, i }) {
  return (
    <span className="block overflow-hidden">
      <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
        {children}
      </motion.span>
    </span>
  );
}

const PILL_ICONS = { aeroport: Plane, disposition: Clock, mariage: Heart, affaires: Briefcase, autre: Sparkles };

function goToBooking(serviceTitle) {
  if (serviceTitle) {
    window.dispatchEvent(new CustomEvent("oka:preselect-service", { detail: serviceTitle }));
  }
  document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
}

export default function Hero() {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section id="top" data-testid="hero" className="relative min-h-screen bg-[#0B132B] grain overflow-hidden">
      {/* subtle radial spotlight */}
      <div className="absolute inset-0 z-0" style={{ background: "radial-gradient(120% 80% at 50% 100%, rgba(60,74,114,0.55) 0%, rgba(11,19,43,0) 60%)" }} />

      {/* Aurora glow blobs */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="aurora aurora-a w-[45vw] h-[45vw] -top-[10vw] -left-[8vw]" style={{ background: "rgba(60,74,114,0.5)" }} />
        <div className="aurora aurora-b w-[38vw] h-[38vw] top-[8vw] right-[-6vw]" style={{ background: "rgba(142,162,200,0.28)" }} />
      </div>

      {/* Fine grid overlay */}
      <div className="absolute inset-0 z-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(#8ea2c8 1px, transparent 1px), linear-gradient(90deg, #8ea2c8 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 pt-36 md:pt-44">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex flex-wrap items-center gap-x-6 gap-y-3 text-[#8ea2c8] text-xs tracking-[0.35em] uppercase mb-8"
        >
          <span className="flex items-center gap-3">
            <MapPin className="w-4 h-4" />
            Chauffeur privé VTC à Lyon — {CONTACT.zone}
          </span>
          <span className="flex items-center gap-2.5 text-white/60">
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full w-2 h-2 bg-emerald-400" />
            </span>
            Disponible 7j/7
          </span>
        </motion.div>

        <h1 className="font-display text-white text-[15vw] md:text-[11vw] lg:text-[9vw] leading-[0.86] tracking-tighter">
          <Reveal i={0}>Voyagez</Reveal>
          <Reveal i={1}>
            <span className="italic font-normal text-[#8ea2c8]">l'élégance</span>
          </Reveal>
          <Reveal i={2}>en toute sérénité</Reveal>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="max-w-md text-white/70 text-base md:text-lg leading-relaxed"
          >
            Transferts aéroport, mise à disposition et véhicule de mariage à bord d'une Peugeot 508 SW Hybrid blanche, à Lyon et ses environs.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="flex flex-col items-start md:items-end gap-4"
          >
            <div className="flex items-center gap-4">
              <button
                data-testid="hero-reserve-btn"
                onClick={() => goToBooking()}
                className="shine-btn bg-white text-[#0B132B] px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#8ea2c8] transition-colors duration-300"
              >
                Réserver une course
              </button>
              <button
                data-testid="hero-services-btn"
                onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                className="text-white border border-white/25 px-8 py-4 text-sm font-semibold tracking-wide hover:border-white transition-colors duration-300"
              >
                Découvrir
              </button>
            </div>
            <p className="text-[11px] tracking-[0.2em] uppercase text-white/40">
              Devis gratuit · Réponse rapide · Sans engagement
            </p>
          </motion.div>
        </div>

        {/* Pastilles de réservation rapide */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="flex flex-wrap gap-2.5 mt-10"
        >
          {SERVICES.map((s) => {
            const Icon = PILL_ICONS[s.key] ?? Sparkles;
            return (
              <button
                key={s.key}
                data-testid={`hero-pill-${s.key}`}
                onClick={() => goToBooking(s.title)}
                className="group flex items-center gap-2 border border-white/20 bg-white/[0.04] backdrop-blur-sm px-4 py-2.5 text-white/85 text-xs font-medium tracking-wide hover:bg-white hover:text-[#0B132B] hover:border-white transition-colors duration-300"
              >
                <Icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            );
          })}
        </motion.div>
      </div>

      {/* Car image with parallax */}
      <motion.div
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 mx-auto max-w-[1200px] px-6 mt-14 md:mt-16"
        style={{ transform: `translateY(${offset * -0.06}px)` }}
      >
        <div className="relative">
          <img
            src={IMAGES.hero}
            alt="Peugeot 508 SW Hybrid blanche à Lyon"
            className="w-full object-cover"
            style={{ maskImage: "linear-gradient(to bottom, black 78%, transparent 100%)" }}
          />

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.6 }}
            className="hidden md:flex items-center gap-3 absolute left-6 lg:left-10 top-8 lg:top-10 bg-white/95 backdrop-blur px-5 py-3.5 shadow-xl"
          >
            <span className="w-9 h-9 bg-[#0B132B] text-white flex items-center justify-center shrink-0">
              <Car className="w-4 h-4" />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#1C2541]/50">Véhicule</div>
              <div className="font-semibold text-[#0B132B] text-sm">Peugeot 508 SW Hybrid 180 · 2025</div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-white/50 flex flex-col items-center gap-2">
        <span className="text-[10px] tracking-[0.3em] uppercase">Défiler</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </div>
    </section>
  );
}
